import axios from 'axios';
import OrgUserAuthService from './orgUserAuth.service';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const OrgUserService = {
  /**
   * Get dashboard statistics
   */
  getDashboardStats: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/dashboard/stats`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch dashboard stats' };
    }
  },

  /**
   * Get OrgUser profile
   */
  getProfile: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/auth/profile`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch profile' };
    }
  },

  /**
   * Update OrgUser profile
   */
  updateProfile: async (profileData) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/orguser/auth/profile`,
        profileData,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update profile' };
    }
  },

  /**
   * Get credit checks
   */
  getCreditChecks: async (page = 1, limit = 10, search = '', status = '', fromDate = '', toDate = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-checks`,
        {
          params: { page, limit, search, status, fromDate, toDate },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch credit checks' };
    }
  },

  /**
   * Create PAN verification
   */
  createPanVerification: async (panNumber) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/pan-verifications`,
        { panNumber },
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create PAN verification' };
    }
  },

  /**
   * Get PAN verifications
   */
  getPanVerifications: async (page = 1, limit = 10, search = '', fromDate = '', toDate = '', status = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/pan-verifications`,
        {
          params: { page, limit, search, fromDate, toDate, status },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch PAN verifications' };
    }
  },

  /**
   * Download PAN verification report PDF
   */
  downloadPanVerificationReport: async (verificationId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/pan-verifications/${verificationId}/download`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download PAN report' };
    }
  },

  /**
   * Get credit check by ID
   */
  getCreditCheckById: async (checkId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/credit-check/${checkId}`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch credit check' };
    }
  },

  /**
   * Get credit reports
   */
  getCreditReports: async (page = 1, limit = 10, search = '', grade = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-reports`,
        {
          params: { page, limit, search, grade },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch credit reports' };
    }
  },

  /**
   * Get credit report by ID
   */
  getCreditReportById: async (reportId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-reports/${reportId}`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch credit report' };
    }
  },

  /**
   * Download credit report PDF
   */
  downloadCreditReport: async (checkId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-checks/${checkId}/download`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download credit report' };
    }
  },

  /**
   * Get credit report PDF readiness status
   */
  getCreditReportPdfStatus: async (checkId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-checks/${checkId}/pdf-status`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to check credit report PDF status' };
    }
  },

  /**
   * Get wallet information
   */
  getWalletInfo: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/wallet`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch wallet info' };
    }
  },

  /**
   * Get wallet transactions
   */
  getWalletTransactions: async (page = 1, limit = 10, type = '', startDate = '', endDate = '') => {
    try {
      const params = { page, limit };
      if (type) params.type = type;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get(
        `${API_BASE_URL}/orguser/wallet/transactions`,
        {
          params,
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch wallet transactions' };
    }
  },

  /**
   * Download wallet statement
   */
  downloadWalletStatement: async (type = '', startDate = '', endDate = '') => {
    try {
      const params = {};
      if (type) params.type = type;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get(
        `${API_BASE_URL}/orguser/wallet/statement`,
        {
          params,
          headers: OrgUserAuthService.getAuthHeader(),
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download wallet statement' };
    }
  },

  /**
   * Confirm wallet top-up after Razorpay payment success
   */
  confirmWalletTopup: async ({ topupRequestId, razorpayOrderId, razorpayPaymentId, razorpaySignature }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/wallet/topup/confirm`,
        { topupRequestId, razorpayOrderId, razorpayPaymentId, razorpaySignature },
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to confirm wallet top-up' };
    }
  },

  /**
   * Initiate wallet top-up and receive Razorpay order details
   */
  initiateWalletTopup: async (amount) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/wallet/topup/initiate`,
        { amount },
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to initiate wallet top-up' };
    }
  },

  /**
   * Download invoice for a top-up transaction
   */
  downloadWalletTopupInvoice: async (transactionId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/wallet/topup/${transactionId}/invoice`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download wallet invoice' };
    }
  },

  /**
   * Get configured credit costs
   */
  getCreditCosts: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/credit-costs`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch credit costs' };
    }
  },

  /**
   * Create KYC verification (Aadhaar eKYC, ITR Download, DL, RC)
   */
  createKycVerification: async (kycType, payload) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/kyc/${kycType}`,
        payload,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create KYC verification' };
    }
  },

  /**
   * Get KYC verifications
   */
  getKycVerifications: async (kycType, page = 1, limit = 10, search = '', fromDate = '', toDate = '', status = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/kyc/${kycType}`,
        {
          params: { page, limit, search, fromDate, toDate, status },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch KYC verifications' };
    }
  },

  /**
   * Download KYC verification report (stub)
   */
  downloadKycVerificationReport: async (kycType, verificationId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/kyc/${kycType}/${verificationId}/download`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download KYC report' };
    }
  },

  /**
   * Fetch court case KYC results (step 2)
   */
  fetchKycCourtCaseResults: async (verificationId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/kyc/court-case-check/${verificationId}/fetch`,
        {},
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch court case results' };
    }
  },

  /**
   * Fetch DL KYC results (step 2 for Deepvue)
   */
  fetchKycDlVerificationResults: async (verificationId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/kyc/dl-verification/${verificationId}/fetch`,
        {},
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch DL verification results' };
    }
  },

  /**
   * Get support tickets
   */
  getSupportTickets: async (page = 1, limit = 10, status = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/support-tickets`,
        {
          params: { page, limit, status },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch support tickets' };
    }
  },

  /**
   * Create support ticket
   */
  createSupportTicket: async (ticketData) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/support-tickets`,
        ticketData,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create support ticket' };
    }
  },

  /**
   * Get activity logs
   */
  getActivityLogs: async (page = 1, limit = 20, action = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/activity-logs`,
        {
          params: { page, limit, action },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch activity logs' };
    }
  },

  /**
   * Get organization details
   */
  getOrganizationDetails: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/organization`,
        {
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch organization details' };
    }
  },

  /**
   * Get organization users list
   */
  getOrgUsersList: async (page = 1, limit = 10, search = '') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orguser/organization/users`,
        {
          params: { page, limit, search },
          headers: OrgUserAuthService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch org users list' };
    }
  },
};

export default OrgUserService;
